import { useNavigate, useParams, useRouterState } from '@tanstack/react-router'
import type {ViewType} from '@/store/ProjectViewPreferencesStore';
import useProjectViewPreferencesStore from '@/store/ProjectViewPreferencesStore'

const SPACE_VIEW_ROUTE = /\/spaces\/[^/]+\/(list|board|calendar|gantt)\/?$/

/**
 * The current view type for a project or space. Space views are separate
 * routes, so on those the URL wins; otherwise the stored preference.
 */
export function useCurrentViewType(projectId: string): ViewType {
  const stored = useProjectViewPreferencesStore(
    (s) => s.preferences[projectId]?.viewType ?? 'list',
  )
  const routeView = useRouterState({
    select: (s) => (SPACE_VIEW_ROUTE.exec(s.location.pathname)?.[1] ?? null) as ViewType | null,
  })
  return routeView ?? stored
}

/**
 * Returns a function that updates the stored view type for a project.
 */
export function useSetViewType(projectId: string) {
  const setViewType = useProjectViewPreferencesStore((s) => s.setViewType)
  return (viewType: ViewType) => setViewType(projectId, viewType)
}

// Space views are separate routes, so switching layout must navigate (the
// project view, by contrast, content-switches in place).
const SPACE_VIEW_ROUTES = {
  list: '/$workspaceSlug/spaces/$spaceKey/list',
  board: '/$workspaceSlug/spaces/$spaceKey/board',
  calendar: '/$workspaceSlug/spaces/$spaceKey/calendar',
  gantt: '/$workspaceSlug/spaces/$spaceKey/gantt',
} as const

/**
 * Returns a function that switches view type: persists the preference (drives
 * the Tasks-tab default + base redirect) and, for spaces, navigates to that
 * view's route so the page actually changes.
 */
export function useChangeViewType(entityId: string, isSpace: boolean) {
  const setViewType = useSetViewType(entityId)
  const navigate = useNavigate()
  const params = useParams({ strict: false })

  return (next: ViewType) => {
    setViewType(next)
    const { workspaceSlug, spaceKey } = params
    if (isSpace && workspaceSlug && spaceKey) {
      void navigate({ to: SPACE_VIEW_ROUTES[next], params: { workspaceSlug, spaceKey } })
    }
  }
}
